<template>
    <vx-table-row>
        <vx-table-row-title>
            {{ model.email }}
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Team Role: </template>
            {{ TEAM_USER_ROLES[model.role] || '-' }}
        </vx-table-row-item>

         <vx-table-row-item v-if="FEATURES.contact_groups">
            <template v-slot:label>  OU Invites: </template>
            {{ model.org_unit_invite_count }}
        </vx-table-row-item>

        <vx-table-row-item v-if="FEATURES.contact_groups">
            <template v-slot:label> Group Invites: </template>
            {{ model.group_invite_count }}
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            {{ model.status === 'pending' ? 'Pending' : 'Expired' }}
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Invited At: </template>
            <span :class="invitedAtColor" :title="shortDateTime(model.updated_at)" :key="invitedAtKey">
                {{ fromNow(model.updated_at) }}
            </span>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="isAdminOrOwner && !props.readOnly">
            <vx-dropdown placement="right">
                <vx-button data-test="dropdown"
                    type="button"
                    color="muted"
                    outline
                    class="w-[45px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item v-if="FEATURES.contact_groups"
                        data-test="invite-to-org-nit"
                        @click="emit('inviteToOrgUnit', model)">
                        Invite to OU
                        <font-awesome-icon widthAuto :icon="faBuildingFlag"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-if="FEATURES.contact_groups"
                        data-test="invite-to-group"
                        @click="emit('inviteToGroup', model)">
                        Invite to Group
                        <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item
                        data-test="delete"
                        @click="deleteInvite()">
                        Cancel Invite
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>


            <vx-button
                v-if="!props.readOnly"
                data-test="resend"
                @click="resendInvite()"
                color="muted"
                outline>
                Re-send <font-awesome-icon widthAuto :icon="faPaperPlane" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import {
    faPaperPlane,
    faTrash,
    faEllipsis,
    faGrid2Plus,
    faBuildingFlag,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed, ref, inject } from "vue";
import { TEAM_USER_ROLES } from "~/constants/roles";
import dayjs from '~/utils/dayjs';
import { useInterval } from '@vueuse/core';
import { fromNow, shortDateTime } from "~/utils/date";
import { invitationsClient } from "~/services";
import { throwSwalRequestError } from "~/utils/request";
import { useToasted } from "~/composables/useToasted";

const toasted = useToasted();

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const isAdminOrOwner = Spark.isAdminOrOwner;
const FEATURES = inject('FEATURES');

const model = defineModel({
    type: Object,
    required: true,
});

const emit = defineEmits(['inviteToGroup', 'deleted']);

const deleteInvite = (emitDeleted = true) => {
    return VxSwal.confirmation(
        'Cancel Invite',
        'Are you sure? There is no undo.',
        'danger',
        () =>
            invitationsClient.deleteInvite(props.teamId, model.value.id)
                .then(() => {
                    if (emitDeleted) {
                        emit('deleted', model.value.id);
                        toasted.success(`${model.value.email} invite canceled`);
                    }
                })
                .catch(throwSwalRequestError)
    );
};

const resending = ref(false);

const resendInvite = async () => {
    if (resending.value) {
        return;
    }

     return VxSwal.confirmation(
        'Re-send Invite',
        'Are you sure you want to re-send the invite?',
        'success',
        () => invitationsClient.resendInvite(props.teamId, model.value.id)
            .then((response) => {
                model.value = response.data;
                emit('resent', model.value);
                toasted.success('Invitation re-sent successfully');
            })
            .catch(throwSwalRequestError)
            .finally(() => {
                resending.value = false;
            })
    )
};

const invitedAtKey = useInterval(60000)
const invitedAtColor = computed(() => {
    if (model.value.status !== 'pending') {
        return "text-rose-600";
    }

    const diffInDays = dayjs().diff(model.value.updated_at, "days");

    if (diffInDays > 5) {
        return "text-amber-600";
    }

    return "text-emerald-700";
});
</script>
